import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ChevronRight, Compass, ExternalLink, Info, LifeBuoy,
  LifeBuoy as GuideIcon, MessageCircleQuestion, Newspaper, PanelLeft, PanelRight,
  Send, Sparkles, Ticket, X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { helpdeskUrl } from '@/lib/helpdesk';
import { useAuth } from '@/auth/useAuth';
import { useConnectivity } from '@/offline/useConnectivity';
import { httpStatus, validationMessage, writeErrorMessage } from '@/lib/apiError';
import { useGuide } from './useGuide';
import { usePanelPlacement, PANEL_WIDTH, PEEK_HEIGHT } from './usePanelPlacement';
import { anchorExists, resolveAnchor, safeArea, scrollIntoSafeArea } from './highlight/anchors';
import { Spotlight } from './highlight/Spotlight';
import { askPayload, useGuideAsk } from './useGuideAsk';
import { useGuideAct } from './useGuideAct';
import type { GuideAction, GuideFinding, GuideSeverity, Walkthrough } from './types';

/**
 * The DemoOps Guide panel.
 *
 * Phase 1 is the Guide tab: checks and highlighting, computed in the browser
 * from state the page already holds. Phase 2 adds Learn (authored walkthroughs
 * and "explain this screen", still no model) and Ask (the one thing that
 * reaches a server).
 *
 * Mounted once in RootLayout; renders nothing until a page registers.
 *
 * Closed and docked right, the launcher sits in the bottom-right column with
 * the Read-only pill and NADC.AI (launcherSlot, from CornerStack) instead of
 * being fixed to the same corner as the NADC.AI button (2026-10-28). Docked
 * left — or with no column to sit in — it floats bottom-left at the same
 * height the column rests at.
 */

const SEVERITY: Record<GuideSeverity, { icon: typeof Info; ring: string; text: string }> = {
  blocker: { icon: AlertTriangle, ring: 'border-danger-500/40 bg-danger-500/10', text: 'text-danger-400' },
  warning: { icon: AlertTriangle, ring: 'border-amber-600/40 bg-amber-900/15', text: 'text-amber-300' },
  info: { icon: Info, ring: 'border-graphite-700 bg-graphite-800/60', text: 'text-graphite-400' },
};

/** How often the open panel re-reads the page's findings. */
const POLL_MS = 800;

type Tab = 'guide' | 'learn' | 'ask';

export function GuidePanel({ launcherSlot = null }: { launcherSlot?: HTMLElement | null } = {}) {
  const { open, setOpen, registration, spotlight, showAnchor } = useGuide();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { isOnline } = useConnectivity();
  const panelRef = useRef<HTMLElement | null>(null);
  const { mode, dock, sheetSize, bottomInset, topInset, setDock, avoid } = usePanelPlacement(spotlight !== null);

  const [tab, setTab] = useState<Tab>('guide');
  const [tour, setTour] = useState<{ walkthrough: Walkthrough; step: number } | null>(null);

  // findings() reads live page state through closures, so the panel re-reads on
  // a timer while it is open. Closed, this costs nothing.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setTick((n) => n + 1), POLL_MS);
    return () => clearInterval(t);
  }, [open]);

  // Computed whenever a page is registered, not only while open: the closed
  // launcher badges the blocker count, which is the only signal a user gets
  // that the Guide has something to say. Only the POLLING is gated on `open`.
  const findings = useMemo(
    () => registration?.findings() ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registration, open, /* re-read on tick */ setTick],
  );

  const blockers = findings.filter((f) => f.blocksSubmit);
  const target = spotlight ? resolveAnchor(spotlight) : null;

  // The occlusion rule. Flip the dock away from the target if it would be
  // covered, then scroll it into the area the panel does NOT occupy.
  useEffect(() => {
    if (!spotlight) return;
    const el = resolveAnchor(spotlight);
    if (!el) { showAnchor(null); return; }

    avoid(el.getBoundingClientRect());
    // One frame for the dock flip to land before measuring the panel.
    const raf = requestAnimationFrame(() => {
      const area = safeArea(
        { width: window.innerWidth, height: window.innerHeight },
        panelRef.current?.getBoundingClientRect() ?? null,
        bottomInset,
        topInset,
      );
      scrollIntoSafeArea(el, area);
    });
    return () => cancelAnimationFrame(raf);
  }, [spotlight, avoid, bottomInset, topInset, showAnchor]);

  const act = useCallback(
    (f: GuideFinding) => {
      if (f.action?.kind === 'navigate' && f.action.to) { navigate(f.action.to); return; }
      if (f.anchor) showAnchor(f.anchor);
    },
    [navigate, showAnchor],
  );

  /** Steps whose anchor is not on this page are skipped, not shown as dead ends. */
  const liveSteps = useMemo(
    () => (tour ? tour.walkthrough.steps.filter((s) => anchorExists(s.anchor)) : []),
    [tour],
  );

  const goToStep = useCallback((n: number) => {
    setTour((t) => (t ? { ...t, step: n } : t));
  }, []);

  // Moving through a tour re-points the highlight.
  useEffect(() => {
    if (!tour || liveSteps.length === 0) return;
    const step = liveSteps[Math.min(tour.step, liveSteps.length - 1)];
    if (step) showAnchor(step.anchor);
  }, [tour, liveSteps, showAnchor]);

  const endTour = useCallback(() => { setTour(null); showAnchor(null); }, [showAnchor]);

  // Nothing on this screen has registered with the Guide. Closed, that is the
  // long-standing behaviour: no launcher, nothing rendered (the launcher would
  // promise help the Guide cannot give here). OPEN is new (2026-08-31): Help
  // became a menu entry under More, and a menu entry that silently does
  // nothing on most of the app would be worse than no entry at all — so an
  // explicit open gets the general panel, which says what the Guide is and
  // hands over to the help desk.
  if (!registration) {
    if (!open) return null;
    return (
      <GeneralHelpPanel
        frame={
          mode === 'side'
            ? { top: topInset, bottom: bottomInset, width: PANEL_WIDTH, ...(dock === 'right' ? { right: 0 } : { left: 0 }) }
            : { left: 0, right: 0, bottom: bottomInset, maxHeight: `calc(100vh - ${topInset + bottomInset}px)`, height: '70vh' }
        }
        sheet={mode !== 'side'}
        dock={dock}
        onClose={() => setOpen(false)}
      />
    );
  }

  if (!open) {
    const inColumn = dock === 'right' && launcherSlot !== null;
    const launcher = (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open the DemoOps Guide"
        className={cn(
          'flex h-12 items-center gap-2 rounded-full border border-graphite-600 bg-graphite-800 px-4 text-sm font-medium text-graphite-100 shadow-xl transition-colors hover:border-safety-500 hover:text-white',
          inColumn ? 'pointer-events-auto' : 'fixed z-50 print:hidden',
        )}
        style={inColumn ? undefined : {
          ...(dock === 'right' ? { right: 16 } : { left: 16 }),
          bottom: 'calc(var(--corner-base, 1rem) + var(--corner-lift, 0px))',
        }}
      >
        <LifeBuoy className="h-5 w-5 text-safety-500" />
        Guide
        {blockers.length > 0 && (
          <span className="ml-0.5 rounded-full bg-danger-500 px-1.5 text-xs font-semibold text-white">
            {blockers.length}
          </span>
        )}
      </button>
    );
    return inColumn ? createPortal(launcher, launcherSlot) : launcher;
  }

  const isPeek = mode === 'sheet' && sheetSize === 'peek';
  const currentStep = tour && liveSteps.length > 0 ? liveSteps[Math.min(tour.step, liveSteps.length - 1)] : null;

  const frame =
    mode === 'side'
      ? { top: topInset, bottom: bottomInset, width: PANEL_WIDTH, ...(dock === 'right' ? { right: 0 } : { left: 0 }) }
      : {
          left: 0,
          right: 0,
          bottom: bottomInset,
          maxHeight: `calc(100vh - ${topInset + bottomInset}px)`,
          height: isPeek ? PEEK_HEIGHT : '70vh',
        };

  return (
    <>
      <Spotlight
        target={target}
        label={currentStep ? `${Math.min(tour!.step, liveSteps.length - 1) + 1} of ${liveSteps.length}` : 'Here'}
        onDismiss={() => (tour ? endTour() : showAnchor(null))}
      />

      <aside
        ref={panelRef}
        role="complementary"
        aria-label="DemoOps Guide"
        data-testid="guide-panel"
        data-mode={mode}
        data-dock={dock}
        data-size={isPeek ? 'peek' : 'full'}
        className={cn(
          // Over the spotlight (z-[60]) and the page's sheets; under the top
          // row's menus (the bell's and NADC apps', z-[75]), which this panel
          // leaves usable — see measureTopChrome. GeneralHelpPanel matches.
          'fixed z-[70] flex flex-col border-graphite-700 bg-graphite-900 shadow-2xl',
          mode === 'side'
            ? dock === 'right' ? 'border-l' : 'border-r'
            : 'rounded-t-2xl border-t',
        )}
        style={frame}
      >
        {isPeek ? (
          // Collapsed while something is highlighted — the sheet cannot dodge
          // sideways, so it gets out of the way by shrinking. Mid-tour it keeps
          // the step controls, which are the only thing worth its height.
          currentStep ? (
            <StepBar
              step={currentStep.title}
              index={Math.min(tour!.step, liveSteps.length - 1)}
              total={liveSteps.length}
              onBack={() => goToStep(Math.max(0, tour!.step - 1))}
              onNext={() => (tour!.step >= liveSteps.length - 1 ? endTour() : goToStep(tour!.step + 1))}
              onEnd={endTour}
              compact
            />
          ) : (
            <div className="flex h-full items-center justify-between gap-3 px-4">
              <span className="flex min-w-0 items-center gap-2 text-sm text-graphite-300">
                <Compass className="h-4 w-4 shrink-0 text-safety-500" />
                <span className="truncate">Highlighted on the page below.</span>
              </span>
              <button
                type="button"
                onClick={() => showAnchor(null)}
                className="shrink-0 rounded-md bg-safety-500 px-3 py-2 text-sm font-medium text-white"
              >
                Done
              </button>
            </div>
          )
        ) : (
          <>
            <header className="flex items-start justify-between gap-2 border-b border-graphite-800 p-4">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-base font-semibold text-graphite-100">
                  <LifeBuoy className="h-5 w-5 shrink-0 text-safety-500" /> Guide
                </h2>
                <p className="mt-0.5 truncate text-sm text-graphite-400">{registration.screenTitle}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {mode === 'side' && (
                  <button
                    type="button"
                    onClick={() => setDock(dock === 'right' ? 'left' : 'right')}
                    aria-label={dock === 'right' ? 'Move the Guide to the left' : 'Move the Guide to the right'}
                    className="rounded-md p-2 text-graphite-400 hover:bg-graphite-800 hover:text-graphite-100"
                  >
                    {dock === 'right' ? <PanelLeft className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { endTour(); setOpen(false); }}
                  aria-label="Close the Guide"
                  className="rounded-md p-2 text-graphite-400 hover:bg-graphite-800 hover:text-graphite-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </header>

            {tour && currentStep ? (
              <TourBody
                walkthrough={tour.walkthrough}
                step={currentStep}
                index={Math.min(tour.step, liveSteps.length - 1)}
                total={liveSteps.length}
                onBack={() => goToStep(Math.max(0, tour.step - 1))}
                onNext={() => (tour.step >= liveSteps.length - 1 ? endTour() : goToStep(tour.step + 1))}
                onEnd={endTour}
              />
            ) : (
              <>
                <TabBar tab={tab} onChange={setTab} askEnabled={can('guide.use')} />
                <div className="flex-1 overflow-y-auto p-4">
                  {tab === 'guide' && (
                    <GuideTab
                      registration={registration}
                      findings={findings}
                      onAct={act}
                      onShow={showAnchor}
                      online={isOnline}
                    />
                  )}
                  {tab === 'learn' && (
                    <LearnTab
                      registration={registration}
                      onShow={showAnchor}
                      onStart={(w) => { setTour({ walkthrough: w, step: 0 }); }}
                    />
                  )}
                  {tab === 'ask' && (
                    <AskTab registration={registration} findings={findings} online={isOnline} />
                  )}
                </div>
              </>
            )}
          </>
        )}
      </aside>
    </>
  );
}

// ---------------------------------------------------------------- tab bar

function TabBar({ tab, onChange, askEnabled }: { tab: Tab; onChange: (t: Tab) => void; askEnabled: boolean }) {
  const tabs: { id: Tab; label: string; icon: typeof GuideIcon }[] = [
    { id: 'guide', label: 'Guide', icon: GuideIcon },
    { id: 'learn', label: 'Learn', icon: Sparkles },
    ...(askEnabled ? [{ id: 'ask' as const, label: 'Ask', icon: MessageCircleQuestion }] : []),
  ];
  return (
    <div className="flex shrink-0 items-center gap-1 border-b border-graphite-800 px-2 py-1.5" role="tablist">
      {tabs.map((t) => {
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => onChange(t.id)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
              tab === t.id ? 'bg-graphite-800 text-graphite-100' : 'text-graphite-400 hover:text-graphite-200',
            )}
          >
            <Icon className="h-4 w-4" /> {t.label}
          </button>
        );
      })}
      {/* Escalation and release notes are shipped features elsewhere — linked,
          not rebuilt inside the panel. */}
      <span className="ml-auto flex items-center gap-1">
        <a
          href="https://nadc1.net/my-tickets"
          target="_blank"
          rel="noreferrer"
          title="My tickets — ask a person"
          aria-label="My tickets — ask a person"
          className="rounded-md p-1.5 text-graphite-400 hover:bg-graphite-800 hover:text-graphite-100"
        >
          <Ticket className="h-4 w-4" />
        </a>
        <a
          href="/whats-new"
          title="What's new"
          aria-label="What's new"
          className="rounded-md p-1.5 text-graphite-400 hover:bg-graphite-800 hover:text-graphite-100"
        >
          <Newspaper className="h-4 w-4" />
        </a>
      </span>
    </div>
  );
}

// ------------------------------------------------------------- guide tab

function GuideTab({
  registration, findings, onAct, onShow, online,
}: {
  registration: NonNullable<ReturnType<typeof useGuide>['registration']>;
  findings: GuideFinding[];
  onAct: (f: GuideFinding) => void;
  onShow: (a: string) => void;
  online: boolean;
}) {
  const actions = registration.actions();
  return (
    <>
      <p className="mb-4 text-sm text-graphite-300">{registration.screenPurpose}</p>

      <ReadinessBanner findings={findings} status={registration.entity.status} entityType={registration.entity.type} />

      {actions.length > 0 && (
        <>
          <h3 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-graphite-500">
            Do it for me
          </h3>
          <ul className="flex flex-col gap-2">
            {actions.map((a) => (
              <ActionRow key={a.name} action={a} online={online} />
            ))}
          </ul>
        </>
      )}

      <h3 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-graphite-500">
        What am I missing?
      </h3>
      {findings.length === 0 ? (
        <p className="rounded-md border border-graphite-700 bg-graphite-800/60 px-3 py-2 text-sm text-graphite-400">
          Nothing to flag on this screen.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {findings.map((f) => (
            <FindingRow key={f.id} finding={f} onAct={() => onAct(f)} />
          ))}
        </ul>
      )}

      <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-graphite-500">
        Show me where
      </h3>
      {/* Dead anchors are dropped rather than rendered as buttons that do
          nothing — the daily-log template is editable, so a field really can
          disappear from under a landmark. */}
      <ul className="flex flex-col gap-1">
        {registration.landmarks.filter((l) => anchorExists(l.anchor)).map((l) => (
          <li key={l.anchor}>
            <button
              type="button"
              onClick={() => onShow(l.anchor)}
              className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm text-graphite-300 hover:bg-graphite-800 hover:text-graphite-100"
            >
              {l.label}
              <ChevronRight className="h-4 w-4 shrink-0 text-graphite-600" />
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

// ------------------------------------------------------------- learn tab

function LearnTab({
  registration, onShow, onStart,
}: {
  registration: NonNullable<ReturnType<typeof useGuide>['registration']>;
  onShow: (a: string) => void;
  onStart: (w: Walkthrough) => void;
}) {
  const sections = registration.explain();
  return (
    <>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-graphite-500">
        Explain this screen
      </h3>
      <div className="flex flex-col gap-3">
        {sections.map((s) => (
          <div key={s.title}>
            <p className="text-sm font-medium text-graphite-100">{s.title}</p>
            <p className="mt-0.5 text-sm text-graphite-400">{s.body}</p>
            {s.anchor && anchorExists(s.anchor) && (
              <button
                type="button"
                onClick={() => onShow(s.anchor!)}
                className="mt-1 text-xs font-medium text-safety-500 hover:underline"
              >
                Show me
              </button>
            )}
          </div>
        ))}
      </div>

      {registration.walkthroughs.length > 0 && (
        <>
          <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-graphite-500">
            Walk me through it
          </h3>
          <ul className="flex flex-col gap-2">
            {registration.walkthroughs.map((w) => (
              <li key={w.id}>
                <button
                  type="button"
                  onClick={() => onStart(w)}
                  className="w-full rounded-md border border-graphite-700 bg-graphite-800/60 px-3 py-2 text-left hover:border-safety-500"
                >
                  <span className="flex items-center justify-between gap-2 text-sm font-medium text-graphite-100">
                    {w.title}
                    <ChevronRight className="h-4 w-4 shrink-0 text-graphite-600" />
                  </span>
                  <span className="mt-0.5 block text-xs text-graphite-400">{w.blurb}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

// --------------------------------------------------------------- ask tab

function AskTab({
  registration, findings, online,
}: {
  registration: NonNullable<ReturnType<typeof useGuide>['registration']>;
  findings: GuideFinding[];
  online: boolean;
}) {
  const [question, setQuestion] = useState('');
  const [reply, setReply] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ask = useGuideAsk();

  async function submit() {
    const q = question.trim();
    if (!q) return;
    setError(null);
    setReply(null);
    try {
      const res = await ask.mutateAsync(askPayload(q, registration, registration.formState(), findings));
      setReply(res.reply);
    } catch (e) {
      setError(
        httpStatus(e) === 503
          ? 'The Guide’s answer service is not switched on yet. Everything on the Guide and Learn tabs still works.'
          : writeErrorMessage(e),
      );
    }
  }

  if (!online) {
    return (
      <p className="rounded-md border border-amber-600/40 bg-amber-900/15 px-3 py-2 text-sm text-amber-300">
        Asking a question needs a connection. The Guide and Learn tabs work offline — everything they show is
        worked out on this device.
      </p>
    );
  }

  return (
    <>
      <p className="mb-3 text-sm text-graphite-400">
        Ask about this screen in your own words. The Guide is told which fields are filled in and what is
        missing — <span className="text-graphite-300">never what you typed</span>.
      </p>
      <textarea
        aria-label="Your question"
        value={question}
        maxLength={500}
        onChange={(e) => setQuestion(e.target.value)}
        rows={3}
        placeholder={askPlaceholder(registration.entity.type)}
        className="w-full rounded-md border border-graphite-600 bg-graphite-800 px-3 py-2 text-sm text-graphite-100 placeholder:text-graphite-500 focus:border-safety-500 focus:outline-none"
      />
      <button
        type="button"
        onClick={submit}
        disabled={ask.isPending || question.trim() === ''}
        className="mt-2 flex items-center gap-1.5 rounded-md bg-safety-500 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        <Send className="h-4 w-4" /> {ask.isPending ? 'Asking…' : 'Ask'}
      </button>

      {error && (
        <p role="alert" className="mt-3 rounded-md border border-danger-500/40 bg-danger-500/10 px-3 py-2 text-sm text-danger-400">
          {error}
        </p>
      )}
      {reply && (
        <div className="mt-3 rounded-md border border-graphite-700 bg-graphite-800/60 px-3 py-2">
          <p className="whitespace-pre-wrap text-sm text-graphite-200">{reply}</p>
          <p className="mt-2 text-xs text-graphite-500">
            Written by a model from the shape of this screen. Check anything it tells you to change.
          </p>
        </div>
      )}
    </>
  );
}

// ------------------------------------------------------------------ tour

function TourBody({
  walkthrough, step, index, total, onBack, onNext, onEnd,
}: {
  walkthrough: Walkthrough;
  step: { title: string; body: string };
  index: number; total: number;
  onBack: () => void; onNext: () => void; onEnd: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-graphite-500">{walkthrough.title}</p>
        <p className="mt-2 text-base font-semibold text-graphite-100">{step.title}</p>
        <p className="mt-1 text-sm text-graphite-300">{step.body}</p>
      </div>
      <StepBar step={step.title} index={index} total={total} onBack={onBack} onNext={onNext} onEnd={onEnd} />
    </div>
  );
}

function StepBar({
  step, index, total, onBack, onNext, onEnd, compact = false,
}: {
  step: string; index: number; total: number;
  onBack: () => void; onNext: () => void; onEnd: () => void; compact?: boolean;
}) {
  return (
    <div className={cn('flex shrink-0 items-center gap-2 border-t border-graphite-800 px-4', compact ? 'h-full' : 'py-3')}>
      <span className="min-w-0 flex-1 text-xs text-graphite-400">
        {compact ? <span className="block truncate text-graphite-300">{step}</span> : null}
        Step {index + 1} of {total}
      </span>
      <button
        type="button" onClick={onEnd}
        className="rounded-md px-2 py-1.5 text-xs text-graphite-400 hover:text-graphite-100"
      >
        End
      </button>
      <button
        type="button" onClick={onBack} disabled={index === 0}
        className="rounded-md border border-graphite-600 px-3 py-1.5 text-sm text-graphite-200 disabled:opacity-40"
      >
        Back
      </button>
      <button
        type="button" onClick={onNext}
        className="rounded-md bg-safety-500 px-3 py-1.5 text-sm font-medium text-white"
      >
        {index >= total - 1 ? 'Finish' : 'Next'}
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ bits

/**
 * Noun + verb per entity kind, so the banner speaks the page's own language:
 * a log is SUBMITTED, an invoice is APPROVED, a request is SENT, an order is
 * ISSUED. Hardcoding "log" here is exactly the bug this map removed.
 */
const READINESS_PHRASES: Record<string, { noun: string; verb: string }> = {
  daily_log: { noun: 'log', verb: 'submitted' },
  invoice: { noun: 'invoice', verb: 'approved' },
  purchase_request: { noun: 'request', verb: 'sent' },
  purchase_order: { noun: 'order', verb: 'issued' },
};

/** Example question in the Ask box, in the page's own entity language. */
function askPlaceholder(entityType: string): string {
  const { noun, verb } = READINESS_PHRASES[entityType] ?? { noun: 'record', verb: 'submitted' };
  return `Why can’t this ${noun} be ${verb}?`;
}

function ReadinessBanner({ findings, status, entityType }: { findings: GuideFinding[]; status: string; entityType: string }) {
  if (status !== 'draft' && status !== 'new') return null;
  const { noun, verb } = READINESS_PHRASES[entityType] ?? { noun: 'record', verb: 'submitted' };
  const blockers = findings.filter((f) => f.blocksSubmit);
  if (blockers.length === 0) {
    return (
      <p className="flex items-start gap-2 rounded-md border border-signal-500/40 bg-signal-500/10 px-3 py-2 text-sm text-signal-400">
        <Compass className="mt-0.5 h-4 w-4 shrink-0" />
        {`Nothing is stopping this ${noun} from being ${verb}.`}
      </p>
    );
  }
  return (
    <p className="flex items-start gap-2 rounded-md border border-danger-500/40 bg-danger-500/10 px-3 py-2 text-sm text-danger-400">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      {blockers.length === 1
        ? `One thing is stopping this ${noun} from being ${verb}.`
        : `${blockers.length} things are stopping this ${noun} from being ${verb}.`}
    </p>
  );
}

/**
 * One offered action, with its confirmation step.
 *
 * The panel never performs anything on the first press: the button arms a
 * confirmation that spells out what will happen, and only the second press
 * sends `confirm: true`. The server enforces the same rule independently —
 * this is the courtesy, not the control.
 */
function ActionRow({ action, online }: { action: GuideAction; online: boolean }) {
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const act = useGuideAct();

  // A client-side action (staging rows, pressing the page's own button) does
  // exactly what the user's own click does and carries no new capability.
  const isClientSide = typeof action.run === 'function';

  async function perform() {
    setError(null);
    try {
      if (isClientSide) {
        action.run!();
      } else {
        await act.mutateAsync({ action: action.name, entity_id: action.entityId, confirm: true });
      }
      setDone(true);
      setConfirming(false);
    } catch (e) {
      setError(validationMessage(e) ?? writeErrorMessage(e));
      setConfirming(false);
    }
  }

  if (done) {
    return (
      <li className="rounded-md border border-signal-500/40 bg-signal-500/10 px-3 py-2 text-sm text-signal-400">
        Done — {action.label.toLowerCase()}.
      </li>
    );
  }

  return (
    <li className="rounded-md border border-graphite-700 bg-graphite-800/60 px-3 py-2">
      {confirming ? (
        <>
          <p className="text-sm font-medium text-graphite-100">{action.confirmPrompt}</p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={perform}
              disabled={act.isPending}
              className="rounded-md bg-safety-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {act.isPending ? 'Working…' : action.confirmLabel}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-md px-2 py-1.5 text-sm text-graphite-400 hover:text-graphite-100"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={() => (isClientSide ? perform() : setConfirming(true))}
          disabled={!online && !isClientSide}
          title={!online && !isClientSide ? 'This needs a connection' : undefined}
          className="flex w-full items-center justify-between gap-2 text-left text-sm font-medium text-graphite-100 disabled:opacity-50"
        >
          {action.label}
          <ChevronRight className="h-4 w-4 shrink-0 text-graphite-600" />
        </button>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-danger-400">{error}</p>
      )}
    </li>
  );
}

function FindingRow({ finding, onAct }: { finding: GuideFinding; onAct: () => void }) {
  const s = SEVERITY[finding.severity];
  const Icon = s.icon;
  return (
    <li className={cn('rounded-md border px-3 py-2', s.ring)}>
      <p className={cn('flex items-start gap-2 text-sm font-medium', s.text)}>
        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{finding.title}</span>
      </p>
      {finding.detail && <p className="mt-1 pl-6 text-xs text-graphite-400">{finding.detail}</p>}
      {finding.action && (
        <div className="mt-2 flex items-center gap-2 pl-6">
          <button
            type="button"
            onClick={onAct}
            className="rounded-md border border-graphite-600 bg-graphite-800 px-2.5 py-1.5 text-xs font-medium text-graphite-100 hover:border-safety-500"
          >
            {finding.action.label}
          </button>
          {finding.action.kind === 'navigate' && <ExternalLink className="h-3 w-3 text-graphite-600" />}
        </div>
      )}
    </li>
  );
}


/**
 * The Guide with no page behind it — what "Help" in the menu opens anywhere
 * outside the four screens that register findings (daily log, invoice,
 * purchase request, purchase order).
 *
 * Deliberately small: it explains where the Guide DOES have something to say,
 * and otherwise gets out of the way to the help desk, carrying the page the
 * person is on so whoever triages the ticket can see their screen. It is not
 * a documentation site, and must not grow into one here.
 */
function GeneralHelpPanel({ frame, sheet, dock, onClose }: {
  frame: CSSProperties;
  sheet: boolean;
  dock: 'left' | 'right';
  onClose: () => void;
}) {
  return (
    <aside
      role="complementary"
      aria-label="DemoOps Guide"
      data-testid="guide-panel"
      data-mode={sheet ? 'sheet' : 'side'}
      data-dock={dock}
      className={cn(
        'fixed z-[70] flex flex-col border-graphite-700 bg-graphite-900 shadow-2xl',
        sheet ? 'rounded-t-2xl border-t' : dock === 'right' ? 'border-l' : 'border-r',
      )}
      style={frame}
    >
      <header className="flex items-start justify-between gap-2 border-b border-graphite-800 p-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-base font-semibold text-graphite-100">
            <LifeBuoy className="h-5 w-5 shrink-0 text-safety-500" /> Guide
          </h2>
          <p className="mt-0.5 truncate text-sm text-graphite-400">Help with DemoOps</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the Guide"
          className="shrink-0 rounded-md p-2 text-graphite-400 hover:bg-graphite-800 hover:text-graphite-100"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-sm text-graphite-300">
          The Guide checks the screen you are on and tells you what is missing before
          you submit. It has nothing to add about this page.
        </p>
        <p className="mt-3 text-sm text-graphite-400">
          Open it again on a daily log, an invoice, a purchase request or a purchase
          order and it will walk you through what that screen needs.
        </p>

        <a
          href={helpdeskUrl()}
          className="mt-5 flex items-center gap-3 rounded-xl border border-graphite-700 bg-graphite-800 p-3 text-sm hover:border-safety-500"
        >
          <Ticket className="h-5 w-5 shrink-0 text-safety-500" aria-hidden />
          <span>
            <span className="block font-medium text-graphite-100">Raise a help desk ticket</span>
            <span className="block text-graphite-400">
              The screen you are on is sent with it
            </span>
          </span>
          <ExternalLink className="ml-auto h-4 w-4 shrink-0 text-graphite-500" aria-hidden />
        </a>
      </div>
    </aside>
  );
}
